import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { API, COMPANY } from "../lib/constants";

const SERVICE_OPTIONS = [
  "IDR Software Platform",
  "White-Glove IDR Consulting",
  "Open Negotiation Management",
  "Federal IDR Representation",
  "Appeals & Underpayment Recovery",
  "Training & Advisory",
  "Other / Not Sure",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Inquiry sent. We'll be in touch within one business day.");
      setForm({ name: "", email: "", company: "", phone: "", service_interest: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative bg-white">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-16 sm:pt-28 relative">
          <div className="overline text-slate-500">Contact</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight font-light mt-4 text-slate-900 max-w-3xl leading-[1.05]">
            Let's talk recovery.
          </h1>
          <p className="mt-8 text-lg text-slate-600 max-w-2xl">
            Tell us about your practice and current IDR pain points. We'll respond within
            one business day with a recommended next step.
          </p>
        </div>
      </section>

      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <aside className="lg:col-span-4">
              <div className="overline text-slate-500">Reach us directly</div>
              <ul className="mt-8 space-y-6">
                <li className="flex items-start gap-4">
                  <div className="bg-white border border-slate-200 p-3"><Mail className="h-5 w-5 text-[#0A2540]" /></div>
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Email</div>
                    <a href={`mailto:${COMPANY.email}`} className="font-display text-lg text-slate-900 hover:underline" data-testid="contact-email-link">{COMPANY.email}</a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="bg-white border border-slate-200 p-3"><MapPin className="h-5 w-5 text-[#0A2540]" /></div>
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Location</div>
                    <div className="font-display text-lg text-slate-900">{COMPANY.address}</div>
                  </div>
                </li>
              </ul>

              <div className="mt-12 border border-slate-200 bg-white p-6">
                <div className="overline text-slate-500">What happens next</div>
                <ol className="mt-4 space-y-3 text-sm text-slate-700">
                  <li><span className="text-slate-400 font-mono mr-2">01</span>We review your inquiry within 1 business day.</li>
                  <li><span className="text-slate-400 font-mono mr-2">02</span>30-minute discovery call with an IDR specialist.</li>
                  <li><span className="text-slate-400 font-mono mr-2">03</span>BAA signed; we analyze a sample of EOBs.</li>
                  <li><span className="text-slate-400 font-mono mr-2">04</span>Recovery diagnostic + recommended engagement.</li>
                </ol>
              </div>
            </aside>

            <div className="lg:col-span-8">
              {submitted ? (
                <div data-testid="contact-success" className="border border-emerald-200 bg-white p-12 text-center">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
                  <h2 className="font-display text-3xl font-light mt-6 text-slate-900">Thank you.</h2>
                  <p className="mt-4 text-slate-600 max-w-md mx-auto">
                    Your inquiry is in our queue. An IDR specialist will reach out within one business day.
                  </p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    variant="outline"
                    data-testid="contact-send-another"
                    className="mt-8 rounded-md"
                  >
                    Send another inquiry
                  </Button>
                </div>
              ) : (
                <form
                  data-testid="contact-form"
                  onSubmit={handleSubmit}
                  className="border border-slate-200 bg-white p-8 lg:p-10 space-y-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field id="name" label="Full name *" testid="input-name">
                      <Input id="name" data-testid="input-name" value={form.name} onChange={update("name")} placeholder="Jane Doe" required />
                    </Field>
                    <Field id="email" label="Work email *" testid="input-email">
                      <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={update("email")} placeholder="jane@practice.com" required />
                    </Field>
                    <Field id="company" label="Company / Practice" testid="input-company">
                      <Input id="company" data-testid="input-company" value={form.company} onChange={update("company")} placeholder="Atlantic Emergency Physicians" />
                    </Field>
                    <Field id="phone" label="Phone" testid="input-phone">
                      <Input id="phone" data-testid="input-phone" value={form.phone} onChange={update("phone")} placeholder="(555) 123-4567" />
                    </Field>
                  </div>

                  <Field id="service" label="Service of interest" testid="input-service">
                    <Select
                      value={form.service_interest}
                      onValueChange={(v) => setForm({ ...form, service_interest: v })}
                    >
                      <SelectTrigger data-testid="select-service" className="w-full">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`option-${s}`}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field id="message" label="How can we help? *" testid="input-message">
                    <Textarea
                      id="message"
                      data-testid="input-message"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Tell us about your claim volume, current IDR challenges, and what success looks like..."
                      rows={6}
                      required
                    />
                  </Field>

                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <p className="text-xs text-slate-500">
                      We respect your PHI. No medical information should be shared via this form.
                    </p>
                    <Button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit-button"
                      className="rounded-md bg-[#0A2540] hover:bg-[#0F3A66] text-white h-11 px-6 gap-2"
                    >
                      {submitting ? "Sending..." : (<>Send inquiry <Send className="h-4 w-4" /></>)}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ id, label, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</Label>
      {children}
    </div>
  );
}
